/**
 * AI Data Import (static preview): the entities, fields, sample "AI read" results and the pure
 * review rules (validation, duplicates, counts) behind the screens.
 *
 * Nothing here calls the API. The sample rows stand in for what `data-upload/convert` will hand
 * back once the backend serves these use cases; the review rules are the ones the real flow keeps.
 */
import type {TranslationKey} from '_metronic/i18n/t'

// --- Switch ------------------------------------------------------------------------------

/** One-line switch: false hides the AI Data Import submenu and its route. */
export const AI_DATA_IMPORT_ENABLED = true

// --- Types -------------------------------------------------------------------------------

export type AiImportEntityKey = 'classSubjectTeacher' | 'studentParent'

export type AiImportFieldType = 'text' | 'date' | 'select' | 'phone' | 'cnic' | 'email'

/** An option of a select field: a saved record (shown as-is) or a fixed choice (translated). */
export interface AiImportOption {
  value: string
  label?: TranslationKey
}

export interface AiImportField {
  key: string
  label: TranslationKey
  type: AiImportFieldType
  required?: boolean
  options?: AiImportOption[]
}

export interface AiImportEntity {
  key: AiImportEntityKey
  title: TranslationKey
  description: TranslationKey
  /** Keenicon name (`KTIcon iconName`). */
  icon: string
  /** Entities that must be in Ilmversity first (the order support onboards in). */
  needs: AiImportEntityKey[]
  fields: AiImportField[]
  /** Fields that identify a record: two rows with the same values are the same record. */
  identity: string[]
}

/** One column of the school's file and the field the AI matched it to. */
export interface AiImportColumn {
  source: string
  sample: string
  /** The matched field key, or null when the column is ignored. */
  field: string | null
  /** 0..1, how sure the AI is of the match. */
  confidence: number
  /** Matched by the user, not the AI. */
  edited?: boolean
}

/** A row the AI read, as the review grid edits it. */
export interface AiImportRow {
  id: string
  /** The row's number in the school's file. */
  rowNo: number
  values: Record<string, string>
  /** Cells the AI was unsure of: field key -> the value it read them from. */
  unsure: Record<string, string>
  /** For a record Ilmversity already has: skip it or update it. */
  duplicateAction: 'skip' | 'update'
}

export type AiImportCellError =
  | 'REQUIRED'
  | 'INVALID_MOBILE'
  | 'INVALID_CNIC'
  | 'INVALID_EMAIL'
  | 'INVALID_DATE'
  | 'NOT_IN_LIST'

export type AiImportRowStatus = 'error' | 'repeated' | 'existing' | 'check' | 'ready'

export interface AiImportRowReview {
  status: AiImportRowStatus
  errors: Record<string, AiImportCellError>
  /** The first row with the same identity, when this one repeats it. */
  repeatsRowNo?: number
}

export interface AiImportCounts {
  total: number
  ready: number
  check: number
  error: number
  repeated: number
  existing: number
}

// --- Sample data (stands in for saved records and the AI's read) ----------------------------

const BRANCHES: AiImportOption[] = [{value: 'Main Campus'}, {value: 'City Campus'}]
const LEVELS: AiImportOption[] = [{value: 'Primary'}, {value: 'Middle'}, {value: 'O Level'}]
const CLASSES: AiImportOption[] = [
  {value: 'Grade 4 A'},
  {value: 'Grade 4 B'},
  {value: 'Grade 5 A'},
  {value: 'Grade 6 A'},
]
const GENDERS: AiImportOption[] = [
  {value: 'Male', label: 'DATA_UPLOAD.AI_IMPORT.OPTION.MALE'},
  {value: 'Female', label: 'DATA_UPLOAD.AI_IMPORT.OPTION.FEMALE'},
]
const PARENT_TYPES: AiImportOption[] = [
  {value: 'Father', label: 'DATA_UPLOAD.AI_IMPORT.OPTION.FATHER'},
  {value: 'Mother', label: 'DATA_UPLOAD.AI_IMPORT.OPTION.MOTHER'},
  {value: 'Guardian', label: 'DATA_UPLOAD.AI_IMPORT.OPTION.GUARDIAN'},
]

/** The same two kinds of file as the Data Import menu's Class/Subject/Teacher and Student/Parent. */
export const AI_IMPORT_ENTITIES: AiImportEntity[] = [
  {
    key: 'classSubjectTeacher',
    title: 'MENU.CLASS/SUBJECT/TEACHER',
    description: 'DATA_UPLOAD.AI_IMPORT.ENTITY.CST_DESC',
    icon: 'teacher',
    needs: [],
    identity: ['grade', 'section', 'branch', 'subject'],
    fields: [
      {key: 'level', label: 'GENERAL.LEVEL', type: 'select', required: true, options: LEVELS},
      {key: 'grade', label: 'DATA_UPLOAD.AI_IMPORT.FIELD.GRADE', type: 'text', required: true},
      {key: 'section', label: 'DATA_UPLOAD.AI_IMPORT.FIELD.SECTION', type: 'text', required: true},
      {key: 'branch', label: 'GENERAL.BRANCH', type: 'select', required: true, options: BRANCHES},
      {key: 'subject', label: 'DATA_UPLOAD.AI_IMPORT.FIELD.SUBJECT', type: 'text'},
      {key: 'subjectTeacher', label: 'DATA_UPLOAD.AI_IMPORT.FIELD.SUBJECT_TEACHER', type: 'text'},
      {key: 'inchargeTeacher', label: 'DATA_UPLOAD.AI_IMPORT.FIELD.INCHARGE_TEACHER', type: 'text'},
      {key: 'teacherEmail', label: 'GENERAL.EMAIL', type: 'email'},
    ],
  },
  {
    key: 'studentParent',
    title: 'MENU.STUDENT/PARENT',
    description: 'DATA_UPLOAD.AI_IMPORT.ENTITY.SP_DESC',
    icon: 'people',
    needs: ['classSubjectTeacher'],
    identity: ['regNo'],
    fields: [
      {key: 'regNo', label: 'DATA_UPLOAD.AI_IMPORT.FIELD.REG_NO', type: 'text', required: true},
      {key: 'firstName', label: 'GENERAL.FIRST_NAME', type: 'text', required: true},
      {key: 'lastName', label: 'GENERAL.LAST_NAME', type: 'text'},
      {key: 'class', label: 'DATA_UPLOAD.AI_IMPORT.FIELD.CLASS', type: 'select', required: true, options: CLASSES},
      {key: 'gender', label: 'GENERAL.GENDER', type: 'select', options: GENDERS},
      {key: 'dob', label: 'GENERAL.DATE_OF_BIRTH', type: 'date'},
      {key: 'regDate', label: 'DATA_UPLOAD.AI_IMPORT.FIELD.REG_DATE', type: 'date', required: true},
      {key: 'address', label: 'GENERAL.ADDRESS', type: 'text', required: true},
      {key: 'parentType', label: 'DATA_UPLOAD.AI_IMPORT.FIELD.PARENT_TYPE', type: 'select', required: true, options: PARENT_TYPES},
      {key: 'parentName', label: 'DATA_UPLOAD.AI_IMPORT.FIELD.PARENT_NAME', type: 'text', required: true},
      {key: 'parentMobile', label: 'DATA_UPLOAD.AI_IMPORT.FIELD.PARENT_MOBILE', type: 'phone', required: true},
      {key: 'parentCnic', label: 'DATA_UPLOAD.AI_IMPORT.FIELD.PARENT_CNIC', type: 'cnic'},
    ],
  },
]

export const aiImportEntity = (key: AiImportEntityKey): AiImportEntity =>
  AI_IMPORT_ENTITIES.find((entity) => entity.key === key) ?? AI_IMPORT_ENTITIES[0]

/** The entity's key as a file name part: `studentParent` -> `student-parent`. */
export const entitySlug = (key: AiImportEntityKey): string =>
  key.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)

/** The entity after this one in the onboarding order, or null after the last. */
export const nextAiImportEntity = (key: AiImportEntityKey): AiImportEntity | null => {
  const index = AI_IMPORT_ENTITIES.findIndex((entity) => entity.key === key)
  return AI_IMPORT_ENTITIES[index + 1] ?? null
}

/** Whether a row matches a search: any of its values contains the text, case-insensitively. */
export const rowMatches = (row: AiImportRow, search: string): boolean => {
  const needle = search.trim().toLowerCase()
  return !needle || Object.values(row.values).some((value) => value.toLowerCase().includes(needle))
}

/** Records the school already has, by entity, as their identity key (see `identityOf`). */
export const SAMPLE_EXISTING: Record<AiImportEntityKey, string[]> = {
  classSubjectTeacher: ['grade 4|a|main campus|english'],
  studentParent: ['st-1002'],
}

/** How the AI matched the sample file's columns. */
export const SAMPLE_COLUMNS: Record<AiImportEntityKey, AiImportColumn[]> = {
  classSubjectTeacher: [
    {source: 'Wing', sample: 'Primary', field: 'level', confidence: 0.72},
    {source: 'Class', sample: 'Grade 5', field: 'grade', confidence: 0.96},
    {source: 'Sec', sample: 'A', field: 'section', confidence: 0.88},
    {source: 'Campus', sample: 'Main Campus', field: 'branch', confidence: 0.94},
    {source: 'Subject', sample: 'Mathematics', field: 'subject', confidence: 0.98},
    {source: 'Teacher', sample: 'Sadia Noor', field: 'subjectTeacher', confidence: 0.81},
    {source: 'Class Teacher', sample: 'Amna Riaz', field: 'inchargeTeacher', confidence: 0.77},
    {source: 'Teacher E-mail', sample: 'sadia@school.pk', field: 'teacherEmail', confidence: 0.93},
    {source: 'Room', sample: '104', field: null, confidence: 0.3},
  ],
  studentParent: [
    {source: 'GR No.', sample: 'ST-1001', field: 'regNo', confidence: 0.86},
    {source: 'Student Name', sample: 'Ayesha', field: 'firstName', confidence: 0.95},
    {source: 'Surname', sample: 'Khan', field: 'lastName', confidence: 0.92},
    {source: 'Class/Sec', sample: 'Grade 4 A', field: 'class', confidence: 0.9},
    {source: 'M/F', sample: 'F', field: 'gender', confidence: 0.68},
    {source: 'D.O.B', sample: '12/03/2015', field: 'dob', confidence: 0.94},
    {source: 'Admission Date', sample: '01/04/2021', field: 'regDate', confidence: 0.83},
    {source: 'Home Address', sample: 'House 4, Gulberg', field: 'address', confidence: 0.97},
    {source: 'Relation', sample: 'Father', field: 'parentType', confidence: 0.9},
    {source: 'Father/Guardian', sample: 'Imran Khan', field: 'parentName', confidence: 0.87},
    {source: 'Contact', sample: '0300-1234567', field: 'parentMobile', confidence: 0.92},
    {source: 'NIC', sample: '35202-7654321-3', field: 'parentCnic', confidence: 0.89},
    {source: 'Bus Route', sample: 'R-3', field: null, confidence: 0.2},
  ],
}

type SampleRow = [Record<string, string>, Record<string, string>?]

const cst = (
  level: string,
  grade: string,
  section: string,
  branch: string,
  subject: string,
  subjectTeacher: string,
  inchargeTeacher: string,
  teacherEmail: string
) => ({level, grade, section, branch, subject, subjectTeacher, inchargeTeacher, teacherEmail})

const student = (
  regNo: string,
  firstName: string,
  lastName: string,
  className: string,
  gender: string,
  dob: string,
  regDate: string,
  address: string,
  parentType: string,
  parentName: string,
  parentMobile: string,
  parentCnic: string
) => ({
  regNo,
  firstName,
  lastName,
  class: className,
  gender,
  dob,
  regDate,
  address,
  parentType,
  parentName,
  parentMobile,
  parentCnic,
})

const SAMPLE_ROWS: Record<AiImportEntityKey, SampleRow[]> = {
  classSubjectTeacher: [
    [cst('Primary', 'Grade 5', 'A', 'Main Campus', 'Mathematics', 'Sadia Noor', 'Amna Riaz', 'sadia@school.pk')],
    [cst('Primary', 'Grade 4', 'A', 'Main Campus', 'English', 'Hina Akhtar', 'Bushra Ali', 'hina@school.pk')],
    [
      cst('Mid', 'Grade 6', 'B', 'City Campus', 'Science', 'Kamran Ali', 'Kamran Ali', 'kamran@school.pk'),
      {level: 'Mid'},
    ],
    [cst('Middle', 'Grade 7', '', 'City Campus', 'Urdu', 'Farah Naz', '', 'farah@')],
    [cst('Primary', 'Grade 5', 'A', 'Main Campus', 'Mathematics', 'Sadia Noor', 'Amna Riaz', 'sadia@school.pk')],
    [
      cst('Primary', 'Grade 5', 'B', 'Main Campus', 'Computer', 'Usman Tariq', 'Rabia Khan', 'usman@school.pk'),
      {subject: 'Comp.'},
    ],
  ],
  studentParent: [
    [
      student('ST-1001', 'Ayesha', 'Khan', 'Grade 4 A', 'Female', '2015-03-12', '2021-04-01', 'House 4, Gulberg', 'Father', 'Imran Khan', '0300-1234567', '35202-7654321-3'),
      {gender: 'F'},
    ],
    [student('ST-1002', 'Bilal', 'Ahmed', 'Grade 4 B', 'Male', '2015-07-21', '2021-04-01', 'Street 9, Johar Town', 'Father', 'Tariq Ahmed', '0321-7654321', '35202-1234567-1')],
    [student('ST-1003', 'Hira', '', 'Grade 5', 'Female', '2014-11-02', '', 'Block C, Model Town', 'Mother', 'Sana Bibi', '03001', '3520')],
    [student('', 'Usman', 'Ali', 'Grade 6 A', 'Male', '2013-01-15', '2020-04-01', '', 'Father', 'Ali Raza', '0333-1112223', '')],
    [
      student('ST-1005', 'Zainab', 'Raza', 'Grade 5 A', 'Female', '2014-05-30', '2021-08-16', 'Cantt', 'Guardian', 'Raza Hussain', '0345-9876543', ''),
      {firstName: 'Zaynab', parentType: 'Uncle'},
    ],
    [student('ST-1001', 'Ayesha', 'Khan', 'Grade 4 A', 'Female', '2015-03-12', '2021-04-01', 'House 4, Gulberg', 'Father', 'Imran Khan', '0300-1234567', '35202-7654321-3')],
  ],
}

/** The rows the AI "read" from the sample file, ready for the review grid. */
export const sampleRows = (entity: AiImportEntityKey): AiImportRow[] =>
  SAMPLE_ROWS[entity].map(([values, unsure = {}], index) => ({
    id: `${entity}-${index + 1}`,
    rowNo: index + 2,
    values: {...values},
    unsure: {...unsure},
    duplicateAction: 'skip',
  }))

// --- Review rules ----------------------------------------------------------------------------

const MOBILE = /^\+?\d[\d\s-]{9,15}$/
const CNIC = /^\d{5}-?\d{7}-?\d$/
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

/** Why a cell can't be imported as it is, or null when it can. */
export const cellError = (field: AiImportField, raw: string | undefined): AiImportCellError | null => {
  const value = (raw ?? '').trim()
  if (!value) return field.required ? 'REQUIRED' : null
  switch (field.type) {
    case 'phone':
      return MOBILE.test(value) && value.replace(/\D/g, '').length >= 10 ? null : 'INVALID_MOBILE'
    case 'cnic':
      return CNIC.test(value) ? null : 'INVALID_CNIC'
    case 'email':
      return EMAIL.test(value) ? null : 'INVALID_EMAIL'
    case 'date':
      return ISO_DATE.test(value) && !Number.isNaN(Date.parse(value)) ? null : 'INVALID_DATE'
    case 'select':
      return field.options?.some((option) => option.value === value) ? null : 'NOT_IN_LIST'
    default:
      return null
  }
}

/** A row's identity, lower-cased: the same key means the same record. Empty when incomplete. */
export const identityOf = (entity: AiImportEntity, row: AiImportRow): string => {
  const parts = entity.identity.map((key) => (row.values[key] ?? '').trim().toLowerCase())
  return parts.some((part) => !part) ? '' : parts.join('|')
}

/**
 * Where every row stands. A row with an invalid cell is an error; one that repeats an earlier
 * row of the file must be fixed or removed; one Ilmversity already has waits on skip / update;
 * one the AI was unsure of is worth a look but can be imported.
 */
export const reviewRows = (
  entity: AiImportEntity,
  rows: AiImportRow[],
  existing: string[] = SAMPLE_EXISTING[entity.key]
): Map<string, AiImportRowReview> => {
  const saved = new Set(existing)
  const firstRowNo = new Map<string, number>()
  const result = new Map<string, AiImportRowReview>()

  rows.forEach((row) => {
    const errors: Record<string, AiImportCellError> = {}
    entity.fields.forEach((field) => {
      const error = cellError(field, row.values[field.key])
      if (error) errors[field.key] = error
    })

    const identity = identityOf(entity, row)
    const repeatsRowNo = identity ? firstRowNo.get(identity) : undefined
    if (identity && repeatsRowNo === undefined) firstRowNo.set(identity, row.rowNo)

    let status: AiImportRowStatus = 'ready'
    if (Object.keys(errors).length) status = 'error'
    else if (repeatsRowNo !== undefined) status = 'repeated'
    else if (identity && saved.has(identity)) status = 'existing'
    else if (Object.keys(row.unsure).length) status = 'check'

    result.set(row.id, {status, errors, repeatsRowNo})
  })
  return result
}

/** The chips over the grid. */
export const countRows = (reviews: Map<string, AiImportRowReview>): AiImportCounts => {
  const counts: AiImportCounts = {total: 0, ready: 0, check: 0, error: 0, repeated: 0, existing: 0}
  reviews.forEach(({status}) => {
    counts.total += 1
    counts[status] += 1
  })
  return counts
}

/** Import waits until no row has an error or repeats another. */
export const canImport = (counts: AiImportCounts): boolean =>
  counts.total > 0 && counts.error === 0 && counts.repeated === 0

/** What an import of these rows would do: new records, updated ones, and ones left as they are. */
export const importOutcome = (
  rows: AiImportRow[],
  reviews: Map<string, AiImportRowReview>
): {created: number; updated: number; skipped: number} => {
  let created = 0
  let updated = 0
  let skipped = 0
  rows.forEach((row) => {
    const status = reviews.get(row.id)?.status
    if (status === 'existing') {
      if (row.duplicateAction === 'update') updated += 1
      else skipped += 1
    } else if (status === 'ready' || status === 'check') created += 1
  })
  return {created, updated, skipped}
}

/** The import report as CSV: every row, what happened to it and why. */
export const reportCsv = (
  entity: AiImportEntity,
  rows: AiImportRow[],
  reviews: Map<string, AiImportRowReview>,
  label: (key: TranslationKey) => string
): string => {
  const quote = (value: string) => `"${value.replace(/"/g, '""')}"`
  const header = [
    label('DATA_UPLOAD.AI_IMPORT.REVIEW.COL_ROW'),
    ...entity.fields.map((field) => label(field.label)),
  ]
  header.push(label('GENERAL.STATUS'))
  const lines = rows.map((row) => {
    const review = reviews.get(row.id)
    const status: TranslationKey =
      review?.status === 'existing'
        ? row.duplicateAction === 'update'
          ? 'DATA_UPLOAD.AI_IMPORT.RESULT.UPDATED'
          : 'DATA_UPLOAD.AI_IMPORT.RESULT.SKIPPED'
        : 'DATA_UPLOAD.AI_IMPORT.RESULT.CREATED'
    return [String(row.rowNo), ...entity.fields.map((field) => row.values[field.key] ?? ''), label(status)]
  })
  return [header, ...lines].map((line) => line.map(quote).join(',')).join('\n')
}
